import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO, addMinutes } from 'date-fns';
import { Button } from "@/Components/ui/button";
import { Card, CardContent } from "@/Components/ui/card";

interface Slot {
    id: number;
    field_id: number;
    pen_id: number;
    date: string;
    start_time: string;
    used: boolean;
    disabled: boolean;
    booked?: boolean;
}

interface SlotSelectorProps {
    fieldSlug: string;
    penId: string | null;
    date?: Date;
    slotLength: number;
    onSlotsSelect: (slotIds: number[], slotTimes: Record<number, string>) => void;
}

const SlotSelector: React.FC<SlotSelectorProps> = ({ fieldSlug, penId, date, slotLength, onSlotsSelect }) => {
    const [slots, setSlots] = useState<Slot[]>([]);
    const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
    const [selectedRange, setSelectedRange] = useState<[number, number] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchSlots = async () => {
        if (!penId) return;
        const formattedDate = date?.toLocaleDateString('en-GB').replace(/\//g, '-');
        setIsLoading(true);
        setError(null);
        try {
            let url = `/api/field/${fieldSlug}/slots?pen_id=${penId}`;
            if (date) {
                url += `&date=${formattedDate}`;
            }

            const response = await axios.get(url);
            if (Array.isArray(response.data.slots)) {
                setSlots(response.data.slots);
            } else {
                console.error('API did not return an array:', response.data);
                setError('Unexpected data format received from server');
            }
        } catch (error) {
            console.error('Error fetching slots:', error);
            setError('Failed to load slots. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setSelectedSlot(null);
        setSelectedRange(null);
        fetchSlots();
    }, [fieldSlug, penId, date, slotLength]);

    const isSlotSelectable = (index: number) => {
        if (slots[index].used) return false;
        if (slots[index].booked) return false;
        if (slots[index].disabled) return false;
        for (let i = 1; i < slotLength; i++) {
            if (index + i >= slots.length || slots[index + i].used || slots[index + i].booked) return false;
            if (parseISO(slots[index + i].date) > addMinutes(parseISO(slots[index].date), 30 * i)) return false;
        }
        return true;
    };

    const handleSlotSelect = (index: number) => {
        if (!isSlotSelectable(index)) return;

        const endIndex = Math.min(index + slotLength - 1, slots.length - 1);
        setSelectedRange([index, endIndex]);

        const selectedSlots = slots.slice(index, endIndex + 1);
        const selectedSlotIds = selectedSlots.map(slot => slot.id);

        const slotTimes = selectedSlots.reduce((acc, slot) => {
            acc[slot.id] = format(parseISO(`${slot.date}T${slot.start_time}`), 'HH:mm');
            return acc;
        }, {} as Record<number, string>);

        onSlotsSelect(selectedSlotIds, slotTimes);
    };

    const getSlotStyle = (slot: Slot, index: number) => {
        if (selectedRange && index >= selectedRange[0] && index <= selectedRange[1]) {
            if (slot.used) return 'bg-red-300 text-white cursor-not-allowed';
            if (slot.disabled) return 'bg-red-300 text-white cursor-not-allowed';
            if (slot.booked) return 'bg-red-300 text-white cursor-not-allowed';
            return index === selectedRange[0] ? 'bg-green-500 text-white' : 'bg-green-300 text-white';
        }
        if (slot.used) return 'bg-red-300 text-white cursor-not-allowed';
        if (slot.booked) return 'bg-red-300 text-white cursor-not-allowed';
        if (!isSlotSelectable(index)) return 'bg-gray-300 text-gray-500 cursor-not-allowed';
        return 'bg-brand text-white hover:bg-black';
    };

    if (!penId) {
        return <div>Please select a field to view available slots.</div>;
    }

    if (isLoading) return <div>Loading slots...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Card className="w-full mx-auto">
            <CardContent className="p-4">
                {slots.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2">
                        {slots.map((slot, index) => (
                            <Button
                                key={slot.id}
                                type="button"
                                className={`w-full ${getSlotStyle(slot, index)}`}
                                onClick={() => handleSlotSelect(index)}
                                disabled={!isSlotSelectable(index)}
                            >
                                {format(parseISO(`${slot.date}T${slot.start_time}`), 'HH:mm')}
                            </Button>
                        ))}
                    </div>
                ) : (
                    <div>No slots available for the selected criteria.</div>
                )}
            </CardContent>
        </Card>
    );
};

export default SlotSelector;